import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { canFallBackOffline } from '@/lib/api/offlineFallback';

/**
 * 개인 인사 자료(잔여 연차·연차 사용 내역·자격증).
 *
 * 서버가 진실이다(`GET /api/v1/home/hr`, 로그인 사용자별). 홈의 인사 위젯과 마이페이지가 같은
 * 훅을 써서 요청을 한 번만 보낸다 — 둘이 따로 읽으면 같은 자료를 두 번 받는다.
 *
 * ## 급여·고과는 이 훅이 읽지 않는다
 *
 * 예전에는 함께 실려 와서, 마이페이지를 열기만 해도 금액이 브라우저에 있었다. 탭으로 감춘 것은
 * **가린 것이지 막은 것이 아니었다** — 개발자 도구로 응답을 보면 그대로 있었다. 지금은
 * {@link useUnlockHrSensitive} 가 비밀번호를 다시 받아 별도 요청으로 가져온다.
 * 여기 남은 것은 `hasPayslips`·`hasEvaluations` 뿐이고, 그것으로 홈 위젯이 안내 문구를 정한다.
 *
 * 개발 중 백엔드에 닿지 못하면({@link canFallBackOffline}) 데모 자료로 화면을 연다 —
 * 위젯 배치가 오프라인에서 localStorage 로 물러서는 것과 같은 조건이다.
 */

export type HrLeaveUsage = {
  startDate: string;
  endDate: string;
  days: number;
  leaveType: string;
  /** 본인 자료라 사유까지 온다. 통합 캘린더(남이 보는 화면)에는 없는 값이다. */
  reason: string | null;
};

export type HrProfile = {
  leave: { year: number; totalDays: number; usedDays: number; remainingDays: number } | null;
  /** 기준 해에 시작한 연차 사용 내역. 최근 것이 앞이다. */
  leaveUsages: HrLeaveUsage[];
  licenses: Array<{ name: string; issuer: string | null; acquiredDate: string | null; expiryDate: string | null }>;
  /**
   * 지급받은 소프트웨어 라이선스(캐드 등). <b>키는 오지 않는다</b> — 값은 마이페이지에서 본인
   * 비밀번호를 다시 넣어야 열린다(`useRevealMySoftwareKey`).
   */
  software: Array<{ productName: string; edition: string | null; expiresOn: string | null }>;
  hasPayslips: boolean;
  hasEvaluations: boolean;
  /**
   * 마지막으로 그 탭을 연 뒤에 <b>새로 온 것이 있는가</b>. 위젯의 줄은 이 값을 본다 —
   * `has*` 는 「자료가 있는가」 라 다 읽은 사람에게도 늘 참이고, 늘 서 있는 줄은 배경이 된다.
   */
  payslipUnread: boolean;
  evaluationUnread: boolean;
};

/** 비밀번호를 다시 확인해야 오는 자료. 잠금을 푼 동안만 메모리에 있고 캐시에 남기지 않는다. */
export type HrSensitive = {
  payslips: Array<{ payMonth: string; netAmount: number | null; hasDocument: boolean; documentUrl: string | null }>;
  evaluations: Array<{ year: number; grade: string; score: number | null; note: string | null }>;
};

const HR_PATH = '/v1/home/hr';

export function useHrProfile(): UseQueryResult<HrProfile> {
  const { data: session } = useSession();
  const userId = session?.user?.id ?? null;

  return useQuery({
    queryKey: ['home-hr', userId],
    queryFn: fetchHrProfile,
    enabled: !!userId,
    staleTime: 60_000,
  });
}

/**
 * 급여·고과 잠금 해제. <b>쿼리가 아니라 뮤테이션이다</b> — TanStack Query 의 캐시에 넣으면
 * 화면을 떠난 뒤에도 금액이 메모리에 남고, 개발자 도구의 캐시 뷰에서 그대로 보인다.
 * 결과는 호출한 화면이 상태로 들고 있다가 화면을 벗어나면 함께 사라진다.
 */
export function useUnlockHrSensitive() {
  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<HrSensitive>(`${HR_PATH}/unlock`, { method: 'POST', body: { password } }),
  });
}

/**
 * 「여기까지 봤다」 를 서버에 남긴다 — 홈 위젯의 알림 줄이 이것으로 접힌다.
 *
 * <b>결과를 기다리지 않는다.</b> 실패해도 다음에 알림이 한 번 더 뜰 뿐이라, 이것 때문에 화면이
 * 멈추거나 오류를 띄울 이유가 없다. 브라우저가 아니라 <b>계정</b>에 남기므로 다른 기기에서도 접힌다.
 */
export function markNoticeRead(kind: 'PAYSLIP' | 'EVALUATION' | 'ALLOWANCE'): void {
  void apiFetch<void>(`${HR_PATH}/reads/${kind}`, { method: 'POST' }).catch(() => undefined);
}

async function fetchHrProfile(): Promise<HrProfile> {
  try {
    return await apiFetch<HrProfile>(HR_PATH);
  } catch (error) {
    if (canFallBackOffline(error)) return MOCK_HR_PROFILE;
    throw error;
  }
}


/** 서버가 꺼진 개발 화면용 데모. 백엔드 V7 시드와 같은 값이다. */
export const MOCK_HR_PROFILE: HrProfile = {
  leave: { year: 2026, totalDays: 15, usedDays: 6.5, remainingDays: 8.5 },
  leaveUsages: [
    { startDate: '2026-07-20', endDate: '2026-07-22', days: 3, leaveType: '연차', reason: '가족 여행' },
    { startDate: '2026-05-02', endDate: '2026-05-02', days: 1, leaveType: '연차', reason: null },
    { startDate: '2026-03-14', endDate: '2026-03-14', days: 0.5, leaveType: '반차', reason: '병원' },
    { startDate: '2026-02-09', endDate: '2026-02-10', days: 2, leaveType: '연차', reason: null },
  ],
  licenses: [
    { name: '건설안전기사', issuer: '한국산업인력공단', acquiredDate: '2018-05-10', expiryDate: null },
    { name: '건축기사', issuer: '한국산업인력공단', acquiredDate: '2015-11-20', expiryDate: null },
    { name: '토목품질시험기술사', issuer: '한국산업인력공단', acquiredDate: '2021-08-01', expiryDate: '2026-09-30' },
  ],
  software: [
    { productName: 'AutoCAD', edition: '2026', expiresOn: '2027-03-31' },
    { productName: 'ZW캐드', edition: null, expiresOn: null },
  ],
  hasPayslips: true,
  hasEvaluations: true,
  // 데모에서도 <b>안 읽은 것이 있는</b> 상태를 보여 준다 — 이 위젯의 알림 줄이 그려지는 유일한 조건이다.
  payslipUnread: true,
  evaluationUnread: true,
};
